import { yupResolver } from "@hookform/resolvers/yup";
import { CheckoutStep } from "@omni/types/api/cart/bff/shared/enums/checkoutStep.enum";
import { PaymentMethod } from "@omni/types/components/payment/paymentMethod.model";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";

import { GiftCardPayment } from "@/components/Checkout/Payment/paymentMethods/paymentMethod/giftCardPayment/giftCardPayment.container";
import { useCheckout } from "@/components/Checkout/useCheckout.hook";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { CheckoutStepValidationResult } from "@/redux/modules/api/data/cart/@types/cart.types";
import * as PlaceOrderModule from "@/redux/modules/api/data/checkout/payment/payment.module";
import {
    broadcastMessage,
    clearMessages
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as PaymentService from "@/services/payment/payment.service";
import {
    getPreSelectedPaymentMethod,
    splitPaymentMethods
} from "@/utils/checkout/paymentMethod.utils";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { useMollie } from "../mollie.provider";
import {
    PaymentMethodsFormValidationSchema,
    PaymentMethodsFormValues
} from "../payment.schema";
import { PaymentMethodComponent } from "./paymentMethod/paymentMethod.component";

const FORM_ID = "checkout-payment-methods-form";

type MethodsContainerProps = {
    methods: Array<PaymentMethod>;
};

const PaymentMethodsContainer = ({ methods }: MethodsContainerProps) => {
    const { t } = useGranite();

    const dispatch = useThunkDispatch();
    const { mollie } = useMollie();

    const formMethods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            code: getPreSelectedPaymentMethod(methods)?.code
        },
        resolver: yupResolver(PaymentMethodsFormValidationSchema)
    });

    const [paymentCreated, setPaymentCreated] = useState(false);

    const { validateCheckoutStep } = useCheckout();

    const onSubmit = async ({ code }: PaymentMethodsFormValues) => {
        if (!isDefined(mollie) || !isDefined(code)) return;
        clearMessages();

        PlaceOrderModule.setLoading(dispatch);

        const validationResult = await validateCheckoutStep(
            CheckoutStep.payment,
            true
        );

        if (validationResult !== CheckoutStepValidationResult.Valid) {
            return;
        }

        await PaymentService.createPaymentRequest(mollie, code)
            .then((paymentRequest) =>
                dispatch(PlaceOrderModule.createPayment(paymentRequest))
            )
            .then(() => setPaymentCreated(true))
            .catch((error) => {
                PlaceOrderModule.setError(dispatch, error);

                broadcastMessage({
                    id: "payment-error-message",
                    text: error.message || error.detail,
                    status: "danger",
                    dismissible: true
                });

                return undefined;
            });
    };

    const { isGiftCardPayment, paymentMethods } = useMemo(
        () => splitPaymentMethods(methods),
        [methods]
    );

    const payedWithGiftCard = isGiftCardPayment && isEmpty(paymentMethods);

    if (paymentCreated) {
        return (
            <Row>
                <p className={"col-12"}>
                    {t("checkout.psp.paymentMethods.redirect")}
                </p>
            </Row>
        );
    }

    return (
        <>
            {payedWithGiftCard && (
                <Row>
                    <p className={"col-12"}>
                        {t("checkout.psp.paymentMethods.noPaymentNecessary")}
                    </p>
                </Row>
            )}
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...formMethods}>
                        <form
                            id={FORM_ID}
                            onSubmit={formMethods.handleSubmit(onSubmit)}
                        >
                            <Container>
                                {paymentMethods.map((method) => (
                                    <PaymentMethodComponent
                                        method={method}
                                        key={method.code}
                                    />
                                ))}
                            </Container>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
            {isGiftCardPayment && (
                <GiftCardPayment
                    onSubmit={onSubmit}
                    payedWithGiftCard={payedWithGiftCard}
                    paymentMethods={methods}
                />
            )}
        </>
    );
};

export { PaymentMethodsContainer };
